import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { SelectedFilterChipVm, SelectedFilterGroupVm } from 'app/library/all-users/components/selected-filters-chip/selected-filter-chip.vm';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { AllUsersService } from 'app/library/all-users/services/all-users.service';
import { CertificateRecordStatus } from 'app/library/certificate-record/models/certificate-record-status.model';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { TeamsState } from 'app/library/team/models/teams.state';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const CERTIFICATES_FILTERS_LIST_STORAGE_KEY = 'certificatesFiltersList';

/**
 * Multi-category "Filter" dialog for certificates.
 * Same UI pattern as `FilterByDialogComponent` / `CourseFilterByDialogComponent`.
 */
@Component({
    selector: 'app-certificates-filter-by-dialog',
    templateUrl: './certificates-filter-by-dialog.component.html',
    styleUrls: ['./certificates-filter-by-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'filter-by-dialog-host',
    },
})
export class CertificatesFilterByDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    certificationProviders$: Observable<CertificationProvider[]>;

    @Input()
    teamId: string;

    @Input()
    includeSubteams: boolean;

    @Select(AllUsersState.getEntities)
    users$: Observable<User[]>;

    @Select(TeamsState.getTeamMembersUsers)
    teamMembers$: Observable<GlobalParticipant[]>;

    statuses = CertificateRecordStatus.all;

    profile = this._store.selectSnapshot(UserState.getMyInfo);
    list: IChoosenFilterInterface[] = [];
    removable = true;
    isTeamsFeatureActive = false;
    selectedFiltersGroups: SelectedFilterGroupVm[] = [];
    filterCategoryCounts: Record<string, number> = {};
    isSelectedDateRangeFilter = false;

    filters: {
        name: string;
        translationKey: string;
    }[] = [];

    private readonly _entityTypesByFilterName: Record<string, IChoosenFilterInterface['entity']['type'][]> = {
        Status: ['Status'],
        Certifications: ['certification', 'provider'],
        IssueDate: ['IssueDate'],
        ExpirationDate: ['ExpirationDate'],
        Teams: ['Teams'],
        Users: ['Users'],
        TeamMembers: ['TeamMembers'],
    };

    selectedFilter = new FormControl();

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    constructor(
        public dialogRef: MatDialogRef<CertificatesFilterByDialogComponent>,
        public tagMultiSelectService: TagMultiSelectService,
        public tagService: TagService,
        private _allUsersApiService: AllUsersApiService,
        private _allUsersService: AllUsersService,
        private _featureSwitchpipe: FeatureSwitchPipe,
        private _teamService: TeamService,
        private _store: Store
    ) {
        super();

        this.isTeamsFeatureActive = this._featureSwitchpipe.transform(FeatureSwitchName.Teams);
    }

    async ngOnInit(): Promise<void> {
        this.filters.push(
            { name: 'Status', translationKey: 'GENERAL.PROPERTY.STATUS' },
            { name: 'Certifications', translationKey: 'PAGE.CERTIFICATIONS.TITLE' },
            { name: 'IssueDate', translationKey: 'PAGE.CERTIFICATIONS.LABEL.ISSUE_DATE' },
            { name: 'ExpirationDate', translationKey: 'PAGE.CERTIFICATIONS.LABEL.EXPIRATION_DATE' }
        );

        if (
            (this.isTeamsFeatureActive && this.profile.permissions.includes(AdminPermissions.TEAMS_WRITE)) ||
            this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        ) {
            this.filters.push({ name: 'Teams', translationKey: 'TEAMS.TITLE.PLURAL' });
        }

        if (this.teamId) {
            this._teamService.fetchTeamMembersUsers(this.teamId, this.includeSubteams);
            this.filters.push({ name: 'TeamMembers', translationKey: 'USER.PLURAL' });
        } else {
            this._allUsersService.fetchCertificateOwners('', this.sortBy, this.sortOrder, 5, 1);
            this.filters.push({ name: 'Users', translationKey: 'USER.PLURAL' });
        }

        this._allUsersApiService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._refreshSelectedFiltersPanel();
        });

        this.selectedFilter.setValue(this.filters[0]);
        this.selectFilterCategory(this.filters[0]);
    }

    trackFilterCategory(_: number, item: { name: string }): string {
        return item.name;
    }

    trackSelectedFilterGroup(_: number, group: SelectedFilterGroupVm): string {
        return group.groupKey;
    }

    trackSelectedFilterChip(_: number, chip: SelectedFilterChipVm): string {
        return `${chip.entityType}:${chip.entityId}:${chip.chipLabel}`;
    }

    selectFilterCategory(filter: { name: string; translationKey: string }): void {
        this.selectedFilter.setValue(filter);
        this.isSelectedDateRangeFilter = filter.name === 'IssueDate' || filter.name === 'ExpirationDate';
    }

    addToList(entities: IChoosenFilterInterface[]): void {
        this._allUsersApiService.setFiltersList(CERTIFICATES_FILTERS_LIST_STORAGE_KEY, entities, false);
    }

    removeFilterChip(chip: SelectedFilterChipVm): void {
        this._allUsersApiService.setFiltersList(
            CERTIFICATES_FILTERS_LIST_STORAGE_KEY,
            this.list.filter((item) => !(item.entity.id === chip.entityId && item.entity.type === chip.entityType)),
            false
        );
    }

    save(): void {
        this.dialogRef.close({
            data: {
                status: this.list.filter((entity) => entity.entity.type === 'Status').map((ent) => ent.entity),
                certifications: this.list.filter((entity) => entity.entity.type === 'certification'),
                providers: this.list.filter((entity) => entity.entity.type === 'provider'),
                issueDate: this.list.find((entity) => entity.entity.type === 'IssueDate'),
                expirationDate: this.list.find((entity) => entity.entity.type === 'ExpirationDate'),
                teams: this.list.filter((entity) => entity.entity.type === 'Teams'),
                users: this.list.filter((entity) => entity.entity.type === 'Users' || entity.entity.type === 'TeamMembers'),
            },
        });

        this._allUsersApiService.setFiltersList(CERTIFICATES_FILTERS_LIST_STORAGE_KEY, this.list, true);
    }

    clearFilters(): void {
        // Draft only — persist on Save; Cancel restores from session storage.
        this._allUsersApiService.setFiltersList(CERTIFICATES_FILTERS_LIST_STORAGE_KEY, [], false);
    }

    cancel(): void {
        // Always restore the last saved filters (ignore in-dialog draft).
        this._allUsersApiService.setFiltersFromStorage(CERTIFICATES_FILTERS_LIST_STORAGE_KEY);
        this.dialogRef.close(false);
    }

    private _refreshSelectedFiltersPanel(): void {
        this.selectedFiltersGroups = this._buildGroupedSelectedFilters(this.list);
        this.filterCategoryCounts = this._buildFilterCategoryCounts(this.list);
    }

    private _buildFilterCategoryCounts(list: IChoosenFilterInterface[]): Record<string, number> {
        const counts: Record<string, number> = {};

        for (const filter of this.filters) {
            const entityTypes = this._entityTypesByFilterName[filter.name];
            counts[filter.name] = list.filter((item) => entityTypes?.includes(item.entity.type)).length;
        }

        return counts;
    }

    private _buildGroupedSelectedFilters(list: IChoosenFilterInterface[]): SelectedFilterGroupVm[] {
        const order: string[] = [];
        const map = new Map<string, { categoryLabel: string; chips: SelectedFilterChipVm[] }>();

        for (const item of list) {
            const groupKey = this._selectedFilterGroupKey(item);
            const { category, rest } = this._parseValueToShowPrefix(item.valueToShow);

            if (!map.has(groupKey)) {
                map.set(groupKey, { categoryLabel: category, chips: [] });
                order.push(groupKey);
            }

            map.get(groupKey)!.chips.push({
                chipLabel: rest || item.valueToShow,
                entityId: item.entity.id,
                entityType: item.entity.type,
            });
        }

        const palette = MaxBrainTag.colorsArray;
        return order.map((key, idx) => ({
            groupKey: key,
            categoryLabel: map.get(key)!.categoryLabel,
            color: palette[idx % palette.length],
            chips: map.get(key)!.chips,
        }));
    }

    private _selectedFilterGroupKey(item: IChoosenFilterInterface): string {
        if (item.entity.type === 'certification' || item.entity.type === 'provider') {
            return 'type:Certifications';
        }

        return `type:${item.entity.type}`;
    }

    private _parseValueToShowPrefix(valueToShow: string): { category: string; rest: string } {
        const idx = valueToShow.indexOf(': ');

        if (idx === -1) {
            return { category: valueToShow, rest: '' };
        }

        return { category: valueToShow.slice(0, idx), rest: valueToShow.slice(idx + 2) };
    }
}
